import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import Big from 'big.js'
import {
  selectAllListPools,
  selectLaunchListPools,
  selectListPools,
} from '../../modules/listPools/selectors'
import { parseChainId } from '../../utils'
import { SUPPORTED_CHAINS } from '../../data/supported-chains'
import { numberWithCommas } from '../../utils/commaSeperators'
import { IPoolProps } from '../../types/reducerTypes'
import tempProjectCardImage from './temp-project-card-image.png'
import { isNil } from 'ramda'
import { numberToLocaleString } from '../../utils/numbers'
import { showDecimalsIfNeeded } from '../Dashboard/Allocations/InvestmentInfo'

const useUpcomingProjects = (type: 'labs' | 'launch') => {
  const {
    upcoming,
    active,
    loading: loadingCond,
  } = useSelector(type === 'labs' ? selectListPools : selectLaunchListPools)
  const { pools: allPools, loading: loadingAll } =
    useSelector(selectAllListPools)

  const pools = useMemo(() => {
    if (type === 'labs') {
      const activePools = formatPool(active)
      const upcomingPools = formatPool(upcoming)

      return [...sortPools(activePools), ...sortPools(upcomingPools)]
    }

    const pools = formatPool(allPools)
    return [...sortPools(pools)]
  }, [active, allPools, type, upcoming])

  return useMemo(() => {
    return {
      pools,
      loading: loadingCond || loadingAll,
    }
  }, [loadingAll, loadingCond, pools])
}

export default useUpcomingProjects

function sortPools(pools) {
  return pools.sort((a, b) => {
    // DISCLAIMER: This is chad generated - we should 100% move this ordering to the backend

    const aIsVetted = a?.cmsData?.isVetted
    const bIsVetted = b?.cmsData?.isVetted
    // Sort by isVetted first, placing true values at the beginning
    if (aIsVetted === true && bIsVetted !== true) return -1
    if (bIsVetted === true && aIsVetted !== true) return 1

    // For records with the same isVetted value, sort by startDate, treating null as the latest date
    if (aIsVetted === bIsVetted) {
      const aIsLive = a?.isLive
      const bIsLive = b?.isLive
      if (aIsLive === true && bIsLive !== true) return -1
      if (bIsLive === true && aIsLive !== true) return 1

      const aStartDate = a?.cmsData?.privateSale?.start
      const bStartDate = b?.cmsData?.privateSale?.start
      if (aStartDate === null && bStartDate !== null) return 1 // a is considered later than b
      if (bStartDate === null && aStartDate !== null) return -1 // b is considered later than a
      if (aStartDate === null && bStartDate === null) return 0 // Both are considered equal
      return new Date(aStartDate).getTime() - new Date(bStartDate).getTime() // Both have non-null startDates
    }

    // Default case for handling records not covered above
    return 0
  })
}

export function formatPool(data: IPoolProps[]) {
  return data
    .map((project: any) => {
      const privatePrice = parseFloat(project?.cmsData?.privateSale?.price ?? 1)
      const publicPrice = parseFloat(
        project?.cmsData?.poolSettings?.tgePrice ?? 0,
      )

      if (privatePrice === 0 || publicPrice === 0) {
        return null
      }

      const now = new Date().getTime()
      const end = new Date(project?.cmsData?.privateSale?.end).getTime()
      const isOver = now > end
      // if (isOver) return null

      let timeLeft = ''
      const isDeployed = !!project?.cmsData?.isDeployed
      let isLive = isDeployed
      const hasStart = !isNil(project?.cmsData?.privateSale?.start)
      if (!hasStart || !isLive) {
        timeLeft = 'COMING SOON'
      } else {
        const start = new Date(project?.cmsData?.privateSale?.start).getTime()
        isLive = isDeployed && now > start && now < end

        const isUpcoming = now < start
        if (isUpcoming) {
          const daysUntil = Math.round(
            Math.abs((start - now) / 1000 / 60 / 60 / 24),
          )
          if (daysUntil > 7) {
            const weeks = Math.round(daysUntil / 7)
            timeLeft = `opens in ${weeks} week${weeks === 1 ? '' : 's'}`
          } else {
            timeLeft = `opens in ${daysUntil} day${daysUntil === 1 ? '' : 's'}`
          }
        }
      }

      const { about, media, poolSettings } = project?.cmsData

      const { raiseCurrency } = poolSettings
      const isEthRaise = raiseCurrency === 'eth'

      const logoUrl =
        about?.logo?.url ?? media?.projectThumbnail?.url ?? tempProjectCardImage

      const slug = project?.cmsData?.slug ?? ''

      const chainId = parseChainId(project.cmsData.chainID)
      const chain = SUPPORTED_CHAINS[chainId]
      const chainIconName = chain?.iconName ?? 'blast'

      const formattedHardCap = parseFloat(
        project?.cmsData?.poolSettings?.totalSupplyForSale
          ? (
              privatePrice *
              Number(project?.cmsData?.poolSettings?.totalSupplyForSale)
            ).toFixed(0)
          : project?.params?.cmsDetails?.poolSettings?.totalSupplyForSale
          ? (
              privatePrice *
              Number(
                project?.params?.cmsDetails?.poolSettings?.totalSupplyForSale,
              )
            ).toFixed(0)
          : project.params.totalSupplyForSale
          ? (privatePrice * project.params.totalSupplyForSale).toFixed(0) || '0'
          : '0',
      )

      const isTbc = !!project?.cmsData?.isTbc

      return {
        id: project.id,
        address: project.address,
        timeLeft,
        logoUrl,
        isTimer: isLive,
        cardImageSrc:
          project?.media?.projectThumbnail?.url ?? tempProjectCardImage,
        coinName: project.params.tokenName,
        coinTicker: project.params.tokenSymbol,
        chainIcon: `/img/icons/chains/white/${chainIconName}.svg`,
        cmsData: project?.cmsData,
        descriptionText: about?.description ?? about?.highlightsText ?? '',
        ratio: `1 USD = ${new Big(1).div(privatePrice ?? 1)?.toFixed(2)} 
                ${project?.params?.tokenSymbol?.toUpperCase()}`,
        stats: [
          {
            name: 'Private price',
            value: isTbc
              ? 'TBD'
              : wrapEthOrUsdbLocale(privatePrice, isEthRaise),
          },
          {
            name: 'Launch Price',
            value: isTbc ? 'TBD' : wrapEthOrUsdbLocale(publicPrice, isEthRaise),
          },
          {
            name: 'Hard Cap',
            value: isTbc ? 'TBD' : wrapEthOrUsdb(formattedHardCap, isEthRaise),
          },
        ],
        media,
        slug,
        isLive,
        isOver,
        verifications: {
          kyc: project?.cmsData?.clientVerifications?.kyc,
          doxx: project?.cmsData?.clientVerifications?.doxx,
        },
      }
    })
    .filter(Boolean)
}

export function wrapRewardDisplay({
  value,
  isEthRaise,
  rewardDisplay,
  isDrops = false,
}: {
  value: number
  isEthRaise: boolean
  rewardDisplay: string
  isDrops?: boolean
}) {
  if (isDrops) {
    return `${value}${rewardDisplay ? ` ${rewardDisplay}` : ''}`
  }

  return `${wrapEthOrUsdb(value, isEthRaise)} ${
    rewardDisplay ?? 'Whitelist Amount'
  }`
}

export function wrapEthOrUsdb(value: number, isEthRaise: boolean) {
  return `${showDecimalsIfNeeded(value, isEthRaise ? 7 : 2)}${
    isEthRaise ? ' ETH' : ' USDB'
  }`
}

export function wrapUsd(value: number) {
  return `${showDecimalsIfNeeded(value, 2)} USD`
}

export function wrapEthOrUsdbLocale(value: number, isEthRaise: boolean) {
  return `${value.toLocaleString(undefined, {
    minimumFractionDigits: isEthRaise ? 2 : 0,
    maximumFractionDigits: isEthRaise ? 18 : 2,
  })}${isEthRaise ? ' ETH' : ' USDB'}`
}
